import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { PullToRefresh } from '@/components/PullToRefresh';
import {
  HeartPulse, Search, Send, Loader2, MessageSquare, Stethoscope,
  Plane, ShieldAlert, ChevronDown, ChevronUp, BookOpen, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categoryConfig = {
  'First Aid': { icon: HeartPulse, color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20', dot: 'bg-red-500' },
  'Aeromedical': { icon: Plane, color: 'text-sky-400', bg: 'bg-sky-500/10 border-sky-500/20', dot: 'bg-sky-500' },
};

export default function MedicalCentrePage() {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('ALL');
  const [search, setSearch] = useState('');
  const [selectedTopic, setSelectedTopic] = useState(null);

  // AI Q&A state
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [qaHistory, setQaHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  // AI detail analysis for a topic
  const [analyzingTopic, setAnalyzingTopic] = useState(false);
  const [topicDetail, setTopicDetail] = useState(null);

  const fetchTopics = useCallback(async () => {
    try {
      const url = activeTab !== 'ALL' ? `${API}/medical/topics?category=${activeTab}` : `${API}/medical/topics`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) setTopics(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  const handleRefresh = useCallback(async () => {
    await fetchTopics();
    toast.success('Medical topics refreshed');
  }, [fetchTopics]);

  useEffect(() => { fetchTopics(); }, [fetchTopics]);

  const handleAsk = async () => {
    if (!question.trim()) return;
    setAsking(true);
    setAnswer(null);
    try {
      const res = await fetch(`${API}/medical/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ question: question.trim() })
      });
      if (res.ok) {
        const data = await res.json();
        setAnswer(data);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to get answer');
      }
    } catch {
      toast.error('Request failed');
    } finally {
      setAsking(false);
    }
  };

  const handleTopicDeepDive = async (topic) => {
    setAnalyzingTopic(true);
    setTopicDetail(null);
    try {
      const res = await fetch(`${API}/medical/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          question: `Provide a comprehensive, step-by-step guide on "${topic.title}" including: detailed procedure, equipment needed, common mistakes to avoid, special considerations for aviation/remote settings, and when to escalate. Source from ${topic.source} and other world-leading authorities.`
        })
      });
      if (res.ok) {
        const data = await res.json();
        setTopicDetail(data);
      } else {
        toast.error('Failed to get detailed information');
      }
    } catch {
      toast.error('Request failed');
    } finally {
      setAnalyzingTopic(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API}/medical/qa-history`, { credentials: 'include' });
      if (res.ok) setQaHistory(await res.json());
    } catch {}
  };

  const filtered = topics.filter(t =>
    t.title.toLowerCase().includes(search.toLowerCase()) ||
    t.summary.toLowerCase().includes(search.toLowerCase()) ||
    (t.tags || []).some(tag => tag.includes(search.toLowerCase()))
  );

  return (
    <PullToRefresh onRefresh={handleRefresh} className="min-h-screen">
      <div data-testid="medical-centre-page" className="p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight flex items-center gap-2 sm:gap-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
            <HeartPulse className="h-6 w-6 sm:h-7 sm:w-7 text-red-400" />
            Medical Centre
          </h1>
          <p className="text-slate-500 text-xs sm:text-sm mt-1">
            Evidence-based medical reference &amp; AI-powered Q&amp;A from world authorities
          </p>
        </div>

        {/* AI Question Box */}
        <Card data-testid="medical-question-box" className="bg-slate-900 border-slate-800 shadow-sm">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-3">
              <Stethoscope className="h-4 w-4 text-red-400" />
              <p className="font-mono text-xs uppercase tracking-wider text-slate-400">Ask a Medical Question</p>
              <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[9px] ml-auto">AI-Powered</Badge>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
              data-testid="medical-question-input"
              placeholder="e.g. How do I perform CPR on an infant?"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !asking && handleAsk()}
              className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 flex-1"
            />
            <Button
              data-testid="medical-ask-btn"
              onClick={handleAsk}
              disabled={asking || !question.trim()}
              className="bg-red-600 hover:bg-red-700 text-white px-5 w-full sm:w-auto"
            >
              {asking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>

          {asking && (
            <div className="flex items-center gap-2 mt-4 text-sm text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin text-red-400" />
              Consulting authoritative medical sources...
            </div>
          )}

          {answer && (
            <div data-testid="medical-answer" className="mt-4 bg-slate-950/60 border border-slate-800 rounded-md p-4 animate-fade-in">
              <div className="flex items-start gap-3">
                <div className="h-6 w-6 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MessageSquare className="h-3 w-3 text-red-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-500 mb-2 font-mono">{answer.question}</p>
                  <div className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">{answer.answer}</div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-800/50 flex items-center gap-2">
                <AlertTriangle className="h-3 w-3 text-amber-500" />
                <p className="text-[10px] text-amber-500/70">Reference information only. Not a substitute for professional medical training.</p>
              </div>
            </div>
          )}

          <button
            data-testid="medical-history-toggle"
            onClick={() => { setShowHistory(!showHistory); if (!showHistory) fetchHistory(); }}
            className="flex items-center gap-1.5 mt-3 text-[11px] text-slate-500 hover:text-slate-400 transition-colors"
          >
            {showHistory ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            Previous questions
          </button>

          {showHistory && qaHistory.length > 0 && (
            <ScrollArea className="max-h-48 mt-2">
              <div className="space-y-2">
                {qaHistory.map(qa => (
                  <button
                    key={qa.qa_id}
                    onClick={() => { setQuestion(qa.question); setAnswer(qa); }}
                    className="w-full text-left bg-slate-950/40 border border-slate-800/50 rounded p-3 hover:border-slate-700 transition-colors"
                  >
                    <p className="text-xs text-slate-400 truncate">{qa.question}</p>
                    <p className="text-[10px] text-slate-600 mt-1 line-clamp-1">{qa.answer?.slice(0, 120)}...</p>
                  </button>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Search & Filter */}
      <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
        <div className="relative w-full sm:max-w-md sm:flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input
            data-testid="medical-search"
            placeholder="Search topics..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-slate-900 border-slate-800 text-slate-200 placeholder:text-slate-600"
          />
        </div>
      </div>

      {/* Category Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-900 border border-slate-800 p-1 w-full sm:w-auto flex">
          <TabsTrigger value="ALL" data-testid="med-tab-all" className="flex-1 sm:flex-none data-[state=active]:bg-[#003087] data-[state=active]:text-white text-slate-400 text-xs font-mono">
            ALL
          </TabsTrigger>
          <TabsTrigger value="First Aid" data-testid="med-tab-firstaid" className="flex-1 sm:flex-none data-[state=active]:bg-red-600 data-[state=active]:text-white text-slate-400 text-xs font-mono">
            FIRST AID
          </TabsTrigger>
          <TabsTrigger value="Aeromedical" data-testid="med-tab-aeromedical" className="flex-1 sm:flex-none data-[state=active]:bg-sky-600 data-[state=active]:text-white text-slate-400 text-xs font-mono">
            AEROMEDICAL
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-[140px] sm:h-[160px] bg-slate-900 border border-slate-800 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 sm:py-16">
              <HeartPulse className="h-10 w-10 sm:h-12 sm:w-12 text-slate-700 mx-auto mb-4" />
              <p className="text-slate-500">No medical topics found</p>
              <p className="text-xs text-slate-600 mt-1">Load sample data from the Dashboard or adjust your search</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {filtered.map((topic, idx) => {
                const cat = categoryConfig[topic.category] || categoryConfig['First Aid'];
                const CatIcon = cat.icon;
                return (
                  <Card
                    key={topic.topic_id}
                    data-testid={`medical-topic-${topic.topic_id}`}
                    className={`bg-slate-900 border-slate-800 shadow-sm hover:border-slate-700 transition-all duration-200 cursor-pointer active:scale-[0.98] animate-fade-in stagger-${(idx % 6) + 1}`}
                    style={{ opacity: 0 }}
                    onClick={() => { setSelectedTopic(topic); setTopicDetail(null); }}
                  >
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex items-start gap-2 sm:gap-3">
                        <div className={`h-8 w-8 sm:h-9 sm:w-9 rounded-md flex items-center justify-center flex-shrink-0 ${cat.bg}`}>
                          <CatIcon className={`h-4 w-4 ${cat.color}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={`text-[9px] ${cat.bg} ${cat.color}`}>
                              {topic.category.toUpperCase()}
                            </Badge>
                          </div>
                          <h3 className="text-sm font-medium text-slate-200 mb-1 sm:mb-2 line-clamp-2">{topic.title}</h3>
                          <p className="text-xs text-slate-500 line-clamp-2 sm:line-clamp-3">{topic.summary}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-slate-800/50">
                        <span className="text-[10px] text-slate-600 truncate max-w-[150px]">{topic.source}</span>
                        <BookOpen className="h-3 w-3 text-slate-600 flex-shrink-0" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Topic Detail Dialog */}
      <Dialog open={!!selectedTopic} onOpenChange={() => setSelectedTopic(null)}>
        <DialogContent className="max-w-2xl w-[95vw] sm:w-full bg-[#0f172a] border-slate-700 max-h-[90vh] sm:max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-slate-200 flex items-center gap-2 text-base sm:text-lg pr-6">
              {selectedTopic?.category === 'Aeromedical' ? (
                <Plane className="h-4 w-4 sm:h-5 sm:w-5 text-sky-400 flex-shrink-0" />
              ) : (
                <HeartPulse className="h-4 w-4 sm:h-5 sm:w-5 text-red-400 flex-shrink-0" />
              )}
              <span className="line-clamp-2">{selectedTopic?.title}</span>
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-xs sm:text-sm">
              Source: {selectedTopic?.source}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1 mt-4 pr-2 sm:pr-4">
            <div className="space-y-4">
              {/* Quick Reference */}
              <div className="bg-slate-900/50 border border-slate-800 rounded-md p-3 sm:p-4">
                <p className="font-mono text-[10px] text-slate-500 uppercase tracking-wider mb-2">Quick Reference</p>
                <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">{selectedTopic?.summary}</p>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-1.5">
                {selectedTopic?.tags?.map(tag => (
                  <Badge key={tag} className="bg-slate-800 text-slate-400 border-slate-700 text-[10px]">{tag}</Badge>
                ))}
              </div>

              {/* Deep Dive */}
              <div className="border-t border-slate-800 pt-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                  <p className="font-mono text-[10px] text-slate-500 uppercase tracking-wider flex items-center gap-2">
                    <Stethoscope className="h-3 w-3 text-red-400" /> Detailed Guide (AI-Powered)
                  </p>
                  {!topicDetail && !analyzingTopic && (
                    <Button
                      data-testid="topic-deep-dive-btn"
                      onClick={() => handleTopicDeepDive(selectedTopic)}
                      size="sm"
                      className="bg-red-600 hover:bg-red-700 text-white text-xs w-full sm:w-auto"
                    >
                      Get Full Guide
                    </Button>
                  )}
                </div>

                {analyzingTopic && (
                  <div className="flex items-center gap-2 py-6 justify-center text-xs sm:text-sm text-slate-400">
                    <Loader2 className="h-4 w-4 animate-spin text-red-400" />
                    Compiling authoritative medical guidance...
                  </div>
                )}

                {topicDetail && (
                  <div data-testid="topic-detail-answer" className="bg-slate-900/30 border border-slate-800 rounded-md p-3 sm:p-4 animate-fade-in">
                    <div className="text-xs sm:text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">{topicDetail.answer}</div>
                    <div className="mt-3 pt-3 border-t border-slate-800/50 flex items-center gap-2">
                      <AlertTriangle className="h-3 w-3 text-amber-500 flex-shrink-0" />
                      <p className="text-[10px] text-amber-500/70">Reference information only. Not a substitute for professional medical training.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
      </div>
    </PullToRefresh>
  );
}
